import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { LoadingButton } from '@mui/lab';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Confirm, Notify, Report } from 'notiflix';
import React, { useState } from 'react';
import styled from 'styled-components';
import { isAdmin } from '../../../config/roles';
import { approveInvoice, reconcileInvoice, triggerInvoicePdfDownload } from '../../../data/api/incomes/InvoiceRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const fmtCOP = (cents) => {
    const value = Number(cents ?? 0) / 100;
    return value.toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
    });
};

const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('es-CO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const responseColor = (resp) => {
    switch ((resp || '').toUpperCase()) {
        case 'APPROVED': return 'success';
        case 'DECLINED':
        case 'ERROR': return 'error';
        case 'PENDING': return 'warning';
        case 'VOIDED': return 'default';
        default: return 'default';
    }
};

const InvoiceCard = ({ invoice, onChanged }) => {
    const { auth, token } = useIndex();
    const admin = isAdmin(auth);
    const [reconciling, setReconciling] = useState(false);
    const [approving, setApproving] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const lines = invoice?.invoice_lines ?? invoice?.invoiceLines ?? [];
    const user = invoice?.user;
    const isWompi = invoice?.transaction_origin === 'WOMPI';
    const isFenabocol = invoice?.transaction_origin === 'FENABOCOL';
    const responseUpper = (invoice?.transaction_response || '').toUpperCase();
    const isApproved = responseUpper === 'APPROVED';
    const isTerminal = ['APPROVED', 'DECLINED', 'VOIDED', 'ERROR'].includes(responseUpper);
    const canApprove = admin && isFenabocol && !isApproved;

    const handleReconcile = async (e) => {
        e?.stopPropagation();
        if (reconciling) return;
        setReconciling(true);
        try {
            const { data } = await reconcileInvoice(token, invoice.uuid);
            if (data?.success) {
                Report.success('Reconciliación', data?.message ?? 'Listo.', 'Okay', { textAlign: 'center' });
                onChanged?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setReconciling(false);
        }
    };

    const handleDownloadPdf = async (e) => {
        e?.stopPropagation();
        if (downloading) return;
        setDownloading(true);
        try {
            const filename = `factura-${invoice?.payment_reference || invoice?.uuid}.pdf`;
            await triggerInvoicePdfDownload(token, invoice.uuid, filename);
        } catch (error) {
            Notify.failure('No se pudo descargar el PDF de la factura.');
        } finally {
            setDownloading(false);
        }
    };

    const sendApprove = async () => {
        setApproving(true);
        try {
            const { data } = await approveInvoice(token, invoice.uuid);
            if (data?.success) {
                Report.success('Aprobada', data?.message ?? 'Factura aprobada.', 'Okay', { textAlign: 'center' });
                onChanged?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setApproving(false);
        }
    };

    const handleApprove = (e) => {
        e?.stopPropagation();
        if (approving || !canApprove) return;
        Confirm.show(
            'Confirmar aprobación',
            `¿Aprobar la factura ${invoice?.payment_reference || invoice?.uuid}? Esta acción no se puede deshacer.`,
            'Sí, aprobar',
            'Cancelar',
            sendApprove,
            () => {},
        );
    };

    return (
        <CardWrapper>
            <Accordion sx={{ borderRadius: '12px !important', overflow: 'hidden' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <SummaryRow>
                        <Box className="left">
                            <Box className="icon-box">
                                <ReceiptOutlinedIcon />
                            </Box>
                            <Box className="info">
                                <Typography className="ref">
                                    {invoice?.payment_reference || invoice?.uuid}
                                </Typography>
                                <Typography className="desc" noWrap>
                                    {invoice?.description}
                                </Typography>
                                <Box className="meta">
                                    <Chip size="small" label={invoice?.invoice_type} />
                                    <Chip size="small" variant="outlined" label={invoice?.invoice_origin} />
                                    <Chip size="small" variant="outlined" label={invoice?.transaction_origin} />
                                    {invoice?.transaction_response && (
                                        <Chip
                                            size="small"
                                            color={responseColor(invoice.transaction_response)}
                                            label={invoice.transaction_response}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Box className="right">
                            <Typography className="total">{fmtCOP(invoice?.total)}</Typography>
                            <Typography className="date">{fmtDate(invoice?.created_at)}</Typography>
                        </Box>
                    </SummaryRow>
                </AccordionSummary>

                <AccordionDetails>
                    <ActionsBar>
                        <LoadingButton
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                            loading={downloading}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Descargar PDF
                        </LoadingButton>

                        {admin && (
                            <>
                                {isWompi && (
                                <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    startIcon={<SyncIcon />}
                                    onClick={handleReconcile}
                                    loading={reconciling}
                                    disabled={isTerminal}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    {isTerminal ? 'Estado final (no reconciliable)' : 'Reconciliar con Wompi'}
                                </LoadingButton>
                            )}

                            {canApprove && (
                                <LoadingButton
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleOutlineIcon />}
                                    onClick={handleApprove}
                                    loading={approving}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    Aprobar factura
                                </LoadingButton>
                                )}
                            </>
                        )}
                    </ActionsBar>

                    <DetailGrid>
                        <DetailItem>
                            <span className="label">Subtotal</span>
                            <span className="value">{fmtCOP(invoice?.amount)}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Descuento</span>
                            <span className="value">{fmtCOP(invoice?.discount)}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Impuestos</span>
                            <span className="value">{fmtCOP(invoice?.taxes)}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Total</span>
                            <span className="value strong">{fmtCOP(invoice?.total)}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Moneda</span>
                            <span className="value">{invoice?.currency}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Expira</span>
                            <span className="value">{fmtDate(invoice?.expiration_time)}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Transacción</span>
                            <span className="value">{invoice?.transaction_id || '-'}</span>
                        </DetailItem>
                        <DetailItem>
                            <span className="label">Método</span>
                            <span className="value">{invoice?.transaction_type || '-'}</span>
                        </DetailItem>
                        {user && (
                            <DetailItem className="full">
                                <span className="label">Usuario</span>
                                <span className="value">
                                    {user?.name} {user?.lastname ?? ''} {user?.email ? `· ${user.email}` : ''}
                                </span>
                            </DetailItem>
                        )}
                    </DetailGrid>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography className="lines-title">
                        Líneas de la factura ({lines?.length ?? 0})
                    </Typography>

                    {lines?.length ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Producto</TableCell>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell align="right">Monto</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lines.map((line) => (
                                        <TableRow key={line.uuid ?? line.id}>
                                            <TableCell>{line?.product_name}</TableCell>
                                            <TableCell>{line?.description}</TableCell>
                                            <TableCell align="right">{fmtCOP(line?.amount)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Esta factura no tiene líneas registradas.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        </CardWrapper>
    );
};

export default InvoiceCard;

const CardWrapper = styled.div`
    .MuiAccordion-root {
        border: 1px solid rgba(15, 34, 77, 0.08);
        box-shadow: 0 4px 12px rgba(15, 34, 77, 0.04);

        &::before { display: none; }
    }

    .lines-title {
        font-weight: 900;
        color: #10285f;
        margin-bottom: 0.4rem;
        font-size: 0.9rem;
    }
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 1rem;

    .left {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        min-width: 0;
        flex: 1;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        flex-shrink: 0;
    }

    .info { min-width: 0; }

    .ref {
        color: #10285f;
        font-weight: 900;
        font-size: 0.9rem;
        line-height: 1.1;
        word-break: break-all;
    }

    .desc {
        color: rgba(15, 34, 77, 0.7);
        font-size: 0.82rem;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .meta {
        margin-top: 0.3rem;
        display: flex;
        gap: 0.3rem;
        flex-wrap: wrap;
    }

    .right {
        text-align: right;
        flex-shrink: 0;
    }

    .total {
        color: #10285f;
        font-weight: 900;
        font-size: 1rem;
    }

    .date {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.75rem;
    }

    @media (max-width: 600px) {
        .meta .MuiChip-root { font-size: 0.7rem; height: 22px; }
    }
`;

const ActionsBar = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.6rem;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.6rem;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.5rem 0.6rem;
    border-radius: 10px;
    background: rgba(16, 40, 95, 0.03);

    &.full { grid-column: 1 / -1; }

    .label {
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.72rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.3px;
    }

    .value {
        color: #10285f;
        font-weight: 700;
        font-size: 0.85rem;
        word-break: break-word;
    }

    .value.strong {
        font-weight: 900;
        color: #8a6500;
    }
`;
